/**
 * Pure list transforms behind `loadMessages`: merge the server thread with
 * optimistic bubbles, re-add the pending user message and "thinking" bubble
 * for a task still running, and order the result.
 */
import type { ChatMessage } from '../../types';
import {
  PENDING_USER_MESSAGE_ID_BASE,
  STREAMING_BUBBLE_ID,
  WAITING_BUBBLE_ID,
  type PendingTask,
} from './chatConstants';

type TFn = (key: string, defaultOrVars?: string | Record<string, unknown>, vars?: Record<string, unknown>) => string;

/** Lower-cased, whitespace-collapsed text for "same message" comparisons. */
export function normalizeComparableText(value?: string | null): string {
  return (value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Whether the thread already holds the user message an analysis task posted
 * (mentions the file and the instructions, close to when the task started).
 */
export function hasEquivalentAnalysisMessage(
  list: ChatMessage[],
  filename?: string,
  instructions?: string,
  pendingCreatedAt?: number,
): boolean {
  const normalizedFilename = normalizeComparableText(filename);
  const normalizedInstructions = normalizeComparableText(instructions);
  const recentWindowMs = 15 * 60 * 1000;

  return list.some((message) => {
    if (message.role !== 'user') return false;

    if (pendingCreatedAt) {
      const messageTime = new Date(message.created_at).getTime();
      if (Number.isFinite(messageTime)) {
        const isWithinWindow = Math.abs(messageTime - pendingCreatedAt) <= recentWindowMs;
        if (!isWithinWindow) return false;
      }
    }

    const normalizedContent = normalizeComparableText(message.content);
    if (!normalizedContent) return false;
    if (normalizedFilename && !normalizedContent.includes(normalizedFilename)) return false;
    if (normalizedInstructions && !normalizedContent.includes(normalizedInstructions)) return false;
    return true;
  });
}

/**
 * Server messages (minus any stale bubbles) plus the optimistic messages the
 * server hasn't confirmed yet. `confirmedSome` tells the caller to prune the
 * optimistic store down to `optimisticToAdd`.
 */
export function mergeOptimisticMessages(serverList: ChatMessage[], optimistic: ChatMessage[]) {
  const cleanedList = serverList.filter((m) => m.id !== WAITING_BUBBLE_ID && m.id !== STREAMING_BUBBLE_ID);
  const serverKey = new Set(
    cleanedList.map((m) => `${m.role}:${normalizeComparableText(m.content)}`)
  );
  const optimisticToAdd = optimistic.filter(
    (m) => !serverKey.has(`${m.role}:${normalizeComparableText(m.content)}`)
  );
  const merged = optimisticToAdd.length > 0 ? [...cleanedList, ...optimisticToAdd] : cleanedList;
  const confirmedSome = optimistic.length > 0 && optimisticToAdd.length !== optimistic.length;
  return { merged, optimisticToAdd, confirmedSome };
}

/**
 * While a task runs for this session, show the user message it was started
 * with (unless the server already has it) and the "thinking" bubble. Without
 * a persisted task, the bubble shows only while a send/analysis is running
 * for this very session.
 */
export function withPendingBubbles(
  list: ChatMessage[],
  {
    sessionId,
    pendingTask,
    operationBusyOnSession,
    waitingTipKey,
    t,
    now = () => new Date().toISOString(),
  }: {
    sessionId: number;
    pendingTask: PendingTask | undefined;
    operationBusyOnSession: boolean;
    waitingTipKey: string;
    t: TFn;
    now?: () => string;
  },
): ChatMessage[] {
  let mergedList = list;
  if (pendingTask) {
    let pendingUserContent: string | null = null;
    if (pendingTask.kind === 'chat' && pendingTask.userMessage) {
      pendingUserContent = pendingTask.userMessage;
    } else if (pendingTask.kind === 'analysis') {
      const filename = pendingTask.filename || t('chat.fileFallback');
      pendingUserContent = t('chat.fileUploaded')
        .replace('{{filename}}', filename);
    }

    if (pendingUserContent) {
      const normalizedPending = normalizeComparableText(pendingUserContent);
      const hasUserMessage = mergedList.some(
        (m) =>
          m.role === 'user' &&
          normalizeComparableText(m.content) === normalizedPending
      );
      const hasEquivalentAnalysis =
        pendingTask.kind === 'analysis' &&
        hasEquivalentAnalysisMessage(
          mergedList,
          pendingTask.filename,
          pendingTask.instructions,
          pendingTask.createdAt
        );

      if (!hasUserMessage && !hasEquivalentAnalysis) {
        mergedList = [
          ...mergedList,
          {
            id: PENDING_USER_MESSAGE_ID_BASE - sessionId,
            role: 'user',
            content: pendingUserContent,
            created_at: new Date(pendingTask.createdAt).toISOString(),
          } as ChatMessage,
        ];
      }
    }

    mergedList = [
      ...mergedList,
      {
        id: WAITING_BUBBLE_ID,
        role: 'assistant',
        content: t(waitingTipKey),
        created_at: now(),
      } as ChatMessage,
    ];
  } else if (operationBusyOnSession) {
    mergedList = [
      ...mergedList,
      {
        id: WAITING_BUBBLE_ID,
        role: 'assistant',
        content: t(waitingTipKey),
        created_at: now(),
      } as ChatMessage,
    ];
  }
  return mergedList;
}

/** Sort by created_at, then by id for tiebreaking (in place, like Array.sort). */
export function sortMessagesChronologically(list: ChatMessage[]): ChatMessage[] {
  return list.sort((a, b) => {
    const timeA = new Date(a.created_at).getTime();
    const timeB = new Date(b.created_at).getTime();
    if (timeA !== timeB) return timeA - timeB;
    return a.id - b.id;
  });
}
